/**
 * ClippingsSidebar - 剪藏簿侧边栏共享组件
 * 用于本地剪藏簿和云剪藏簿
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Layout, Button, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { SearchIcon, UsersIcon, FolderIcon, HashIcon, ClockIcon } from 'lucide-react';
import {
  MagnifyingGlassIcon,
  StackOverflowLogoIcon,
  StarIcon,
  CaretDownIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import type { DragDropHandlers, BaseTag, BaseTagGroup, DragTargetType } from '../hooks/useDragDropTags';
import { CHROME_EXTENSION_URL } from '../constants';

const { Sider } = Layout;

// ============ 类型定义 ============

/** 顶部菜单项 */
export interface TopMenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
}

/** 新建菜单额外项 */
export interface CreateMenuItem {
  key: string;
  icon: React.ReactNode;
  label: string | React.ReactNode;
  disabled?: boolean;
}

/** 组件 Props */
export interface ClippingsSidebarProps<
  TTag extends BaseTag = BaseTag,
  TGroup extends BaseTagGroup = BaseTagGroup
> {
  // ===== 数据 =====
  tags: TTag[];
  tagGroups: TGroup[];

  // ===== 选中状态 =====
  /** 当前选中项: 'bloggers' | 'uncategorized' | 'starred' | 'video' | tag.name | `group-${id}` */
  selectedKey: string;
  /** 展开的 group ids */
  expandedGroupIds: string[];

  // ===== 事件回调 =====
  onSelect: (key: string) => void;
  onToggleGroup: (groupId: string) => void;

  // ===== CRUD 操作 =====
  onCreateTag: () => void;
  onCreateGroup: () => void;
  onRenameTag: (tag: TTag) => void;
  onDeleteTag: (tag: TTag) => void;
  onRenameGroup: (group: TGroup) => void;
  onDeleteGroup: (group: TGroup) => void;

  // ===== 拖拽 (可选) =====
  dragDrop?: DragDropHandlers<TTag, TGroup>;

  // ===== 自定义渲染 =====
  /** 渲染标签额外内容（帖子数量、云端图标等） */
  renderTagExtra?: (tag: TTag) => React.ReactNode;
  /** 渲染标签组额外内容 */
  renderGroupExtra?: (group: TGroup) => React.ReactNode;

  // ===== 插槽 =====
  /** 顶部区域（导入进度条等额外内容） */
  headerSlot?: React.ReactNode;
  /** 顶部菜单配置（默认使用内置菜单） */
  topMenuItems?: TopMenuItem[];
  /** 新建菜单额外项 */
  createMenuExtraItems?: CreateMenuItem[];
  onCreateMenuClick?: (key: string) => void;

  // ===== 安装扩展按钮 =====
  /** 是否显示安装扩展按钮（默认 false） */
  showInstallExtensionButton?: boolean;
  /** 点击安装扩展按钮的回调（不提供则使用 window.open） */
  onInstallExtension?: () => void;

  // ===== 宽度调整 (本地端专用) =====
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  onResize?: (width: number) => void;

  // ===== 样式 =====
  className?: string;
  style?: React.CSSProperties;
}

// ============ 默认配置 ============

const DEFAULT_TOP_MENU_ITEMS: TopMenuItem[] = [
  { key: 'bloggers', icon: <UsersIcon size={16} />, label: '已追踪博主' },
  { key: 'recent', icon: <ClockIcon size={16} />, label: '最近剪藏' },
  { key: 'uncategorized', icon: <StackOverflowLogoIcon size={16} weight="bold" />, label: '未分类' },
  { key: 'starred', icon: <StarIcon size={16} weight="bold" />, label: '已加星标' },
  // { key: 'video', icon: <PlayCircleIcon size={16} weight="bold" />, label: '视频帖子' },
];

const DEFAULT_WIDTH = 288;
const MIN_WIDTH = 248;
const MAX_WIDTH = 288;

// ============ 连接线图标 SVG ============
const TreeLineIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M4 3V8.5C4 8.77614 4.22386 9 4.5 9H13.5"
      stroke="var(--ant-color-text-tertiary)"
      strokeWidth="1"
      strokeOpacity="0.8"
    />
  </svg>
);

// ============ 组件实现 ============

export function ClippingsSidebar<
  TTag extends BaseTag,
  TGroup extends BaseTagGroup
>({
  tags,
  tagGroups,
  selectedKey,
  expandedGroupIds,
  onSelect,
  onToggleGroup,
  onCreateTag,
  onCreateGroup,
  onRenameTag,
  onDeleteTag,
  onRenameGroup,
  onDeleteGroup,
  dragDrop,
  renderTagExtra,
  renderGroupExtra,
  headerSlot,
  topMenuItems = DEFAULT_TOP_MENU_ITEMS,
  createMenuExtraItems,
  onCreateMenuClick,
  showInstallExtensionButton = false,
  onInstallExtension,
  width = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  resizable = false,
  onResize,
  className,
  style,
}: ClippingsSidebarProps<TTag, TGroup>) {
  // ===== 状态 =====
  const [hoveredTopMenuItem, setHoveredTopMenuItem] = useState<string | null>(null);
  const [hoveredTagId, setHoveredTagId] = useState<string | null>(null);
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [siderWidth, setSiderWidth] = useState(width);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // ===== 计算 =====

  /** 获取组内标签（按 order 排序）
   * 始终从 tags 数组过滤，确保乐观更新时能反映最新状态
   */
  const getGroupTags = useCallback((group: TGroup): TTag[] => {
    return tags
      .filter(t => t.groupId === group.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tags]);

  /** 未分组标签（过滤掉 __video__ 特殊标签，按 order 排序） */
  const ungroupedTags = useMemo(() => {
    return tags
      .filter(t => !t.groupId && t.name !== '__video__')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tags]);

  // ===== 拖拽相关 =====
  const { draggedItem, isDraggingOver, dropIndicatorPosition } = dragDrop || {
    draggedItem: null,
    isDraggingOver: null,
    dropIndicatorPosition: null,
  };

  const handleDragStart = useCallback((
    e: React.DragEvent,
    type: 'tag' | 'group',
    item: TTag | TGroup
  ) => {
    dragDrop?.onDragStart(e, type, item);
  }, [dragDrop]);

  const handleDragOver = useCallback((
    e: React.DragEvent,
    targetType: DragTargetType,
    targetId: string
  ) => {
    dragDrop?.onDragOver(e, targetType, targetId);
  }, [dragDrop]);

  const handleDragLeave = useCallback(() => {
    dragDrop?.onDragLeave();
  }, [dragDrop]);

  const handleDrop = useCallback((
    e: React.DragEvent,
    targetType: DragTargetType,
    targetId: string
  ) => {
    dragDrop?.onDrop(e, targetType, targetId);
  }, [dragDrop]);

  const handleDragEnd = useCallback(() => {
    dragDrop?.onDragEnd();
    setHoveredTagId(null);
  }, [dragDrop]);

  // ===== 宽度调整 =====
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!resizable) return;
    e.preventDefault();
    setIsResizing(true);
  }, [resizable]);

  React.useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      let newWidth = e.clientX;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;
      setSiderWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      onResize?.(siderWidth);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, siderWidth, minWidth, maxWidth, onResize]);

  // ===== 新建菜单 =====
  const createMenuItems = useMemo(() => {
    const items: any[] = [
      {
        key: 'create-tag',
        icon: <HashIcon size={16} />,
        label: (
          <div>
            <div style={{ fontWeight: 500 }}>创建标签</div>
            <div style={{ fontSize: '12px', color: 'var(--ant-color-text-tertiary)', marginTop: 2 }}>
              用于最基础的分类管理
            </div>
          </div>
        ),
      },
      {
        key: 'create-group',
        icon: <FolderIcon size={16} />,
        label: (
          <div>
            <div style={{ fontWeight: 500 }}>创建标签组</div>
            <div style={{ fontSize: '12px', color: 'var(--ant-color-text-tertiary)', marginTop: 2 }}>
              用于将多个标签组织到一起
            </div>
          </div>
        ),
      },
    ];

    if (createMenuExtraItems && createMenuExtraItems.length > 0) {
      items.push({ type: 'divider' });
      items.push(...createMenuExtraItems);
    }

    return items;
  }, [createMenuExtraItems]);

  const handleCreateMenuClick = useCallback(({ key }: { key: string }) => {
    if (key === 'create-tag') {
      onCreateTag();
    } else if (key === 'create-group') {
      onCreateGroup();
    } else {
      onCreateMenuClick?.(key);
    }
  }, [onCreateTag, onCreateGroup, onCreateMenuClick]);

  // ===== 渲染指示线 =====
  const renderDropIndicator = (id: string, position: 'before' | 'after') => {
    if (dropIndicatorPosition?.id !== id || dropIndicatorPosition.position !== position) {
      return null;
    }
    return (
      <div
        style={{
          position: 'absolute',
          [position === 'before' ? 'top' : 'bottom']: -2,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: 'var(--ant-color-primary)',
          borderRadius: 1,
          zIndex: 1000,
        }}
      />
    );
  };

  // ===== 渲染标签组 =====
  const renderTagGroup = (group: TGroup) => {
    const isExpanded = expandedGroupIds.includes(group.id);
    const isSelected = selectedKey === `group-${group.id}`;
    const isHovered = hoveredGroupId === group.id;
    const groupTags = getGroupTags(group);

    return (
      <div key={group.id} style={{ position: 'relative' }}>
        {/* 上方指示线 */}
        {renderDropIndicator(group.id, 'before')}

        {/* Group 项 */}
        <div
          draggable={!!dragDrop}
          onDragStart={(e) => handleDragStart(e, 'group', group)}
          onDragOver={(e) => handleDragOver(e, 'group-area', group.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'group-area', group.id)}
          onDragEnd={handleDragEnd}
          onMouseEnter={() => setHoveredGroupId(group.id)}
          onMouseLeave={() => setHoveredGroupId(null)}
          onClick={() => onSelect(`group-${group.id}`)}
          style={{
            position: 'relative',
            fontWeight: 600,
            lineHeight: '18px',
            padding: '6px 8px 6px 16px',
            marginBottom: 2,
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isSelected
              ? 'var(--ant-color-fill-secondary)'
              : isDraggingOver === group.id
                ? 'var(--ant-color-fill-tertiary)'
                : isHovered
                  ? 'var(--ant-color-fill-tertiary)'
                  : 'transparent',
            border: isDraggingOver === group.id
              ? '2px dashed var(--ant-color-primary)'
              : '2px solid transparent',
            transition: 'all 0.2s',
            opacity: draggedItem?.id === group.id ? 0.5 : 1,
          }}
        >
          {/* 箭头 */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              onToggleGroup(group.id);
            }}
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '12px',
              color: 'var(--ant-color-text-tertiary)',
              padding: '2px 2px',
            }}
          >
            {isExpanded ? <CaretDownIcon /> : <CaretRightIcon />}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <FolderIcon size={16} style={{ color: 'var(--ant-color-text)', pointerEvents: 'none' }} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: 'var(--ant-color-text)', pointerEvents: 'none' }}>{group.name}</span>
              {renderGroupExtra?.(group)}
            </div>
          </div>

          {/* Hover 时显示操作按钮 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
            }}
          >
            <EditOutlined
              onClick={(e) => {
                e.stopPropagation();
                onRenameGroup(group);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
            <DeleteOutlined
              onClick={(e) => {
                e.stopPropagation();
                onDeleteGroup(group);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
          </div>
        </div>

        {/* 下方指示线 */}
        {renderDropIndicator(group.id, 'after')}

        {/* 组内标签（展开时显示） */}
        {isExpanded && groupTags.length > 0 && (
          <div style={{ paddingLeft: 14, marginBottom: 8 }}>
            {groupTags.map((tag) => renderTagInGroup(tag, group))}
          </div>
        )}
      </div>
    );
  };

  // ===== 渲染组内标签 =====
  const renderTagInGroup = (tag: TTag, group: TGroup) => {
    const isSelected = selectedKey === tag.name;
    const isHovered = hoveredTagId === tag.id;

    return (
      <div key={tag.id} style={{ position: 'relative' }}>
        {/* 上方指示线 */}
        {renderDropIndicator(tag.id, 'before')}

        <div
          draggable={!!dragDrop}
          onDragStart={(e) => handleDragStart(e, 'tag', tag)}
          onDragOver={(e) => handleDragOver(e, 'tag-in-group', tag.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'tag-in-group', tag.id)}
          onDragEnd={handleDragEnd}
          onClick={() => onSelect(tag.name)}
          onMouseEnter={() => setHoveredTagId(tag.id)}
          onMouseLeave={() => setHoveredTagId(current => current === tag.id ? null : current)}
          style={{
            padding: '6px 10px 6px 4px',
            marginBottom: 2,
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--ant-color-text)',
            backgroundColor: isSelected
              ? 'var(--ant-color-fill-secondary)'
              : isHovered
                ? 'var(--ant-color-fill-tertiary)'
                : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            transition: 'background-color 0.2s',
            opacity: draggedItem?.id === tag.id ? 0.5 : 1,
          }}
        >
          {/* 连接线图标 */}
          <TreeLineIcon />

          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{tag.name}</span>
            {renderTagExtra?.(tag)}
          </div>

          {/* Hover 时显示操作按钮 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
              pointerEvents: isHovered ? 'auto' : 'none',
            }}
          >
            <EditOutlined
              onClick={(e) => {
                e.stopPropagation();
                onRenameTag(tag);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
            <DeleteOutlined
              onClick={(e) => {
                e.stopPropagation();
                onDeleteTag(tag);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
          </div>
        </div>

        {/* 下方指示线 */}
        {renderDropIndicator(tag.id, 'after')}
      </div>
    );
  };

  // ===== 渲染未分组标签 =====
  const renderUngroupedTag = (tag: TTag) => {
    const isSelected = selectedKey === tag.name;
    const isHovered = hoveredTagId === tag.id;
    const isDropTarget = isDraggingOver === tag.id && draggedItem?.type === 'tag' && (draggedItem.data as TTag).groupId;

    return (
      <div key={tag.id} style={{ position: 'relative' }}>
        {/* 上方指示线 */}
        {renderDropIndicator(tag.id, 'before')}

        <div
          draggable={!!dragDrop}
          onDragStart={(e) => handleDragStart(e, 'tag', tag)}
          onDragOver={(e) => handleDragOver(e, 'tag', tag.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'tag', tag.id)}
          onDragEnd={handleDragEnd}
          onClick={() => onSelect(tag.name)}
          onMouseEnter={() => setHoveredTagId(tag.id)}
          onMouseLeave={() => setHoveredTagId(current => current === tag.id ? null : current)}
          style={{
            padding: '6px 8px 6px 16px',
            lineHeight: '18px',
            marginBottom: 4,
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--ant-color-text)',
            backgroundColor: isSelected
              ? 'var(--ant-color-fill-secondary)'
              : isDraggingOver === tag.id
                ? 'var(--ant-color-fill-tertiary)'
                : isHovered
                  ? 'var(--ant-color-fill-tertiary)'
                  : 'transparent',
            border: isDropTarget
              ? '2px dashed var(--ant-color-primary)'
              : '2px solid transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            transition: 'all 0.2s',
            opacity: draggedItem?.id === tag.id ? 0.5 : 1,
          }}
        >
          <HashIcon size={14} style={{ color: 'var(--ant-color-text)', pointerEvents: 'none' }} />
          <div style={{ fontWeight: 600, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{tag.name}</span>
            {renderTagExtra?.(tag)}
          </div>

          {/* Hover 时显示操作按钮 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
              pointerEvents: isHovered ? 'auto' : 'none',
            }}
          >
            <EditOutlined
              onClick={(e) => {
                e.stopPropagation();
                onRenameTag(tag);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
            <DeleteOutlined
              onClick={(e) => {
                e.stopPropagation();
                onDeleteTag(tag);
              }}
              style={{ fontSize: '14px', color: 'var(--ant-color-text-tertiary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--ant-color-text-tertiary)'; }}
            />
          </div>
        </div>

        {/* 下方指示线 */}
        {renderDropIndicator(tag.id, 'after')}
      </div>
    );
  };

  // ===== 主渲染 =====
  return (
    <Sider
      className={`main-sider ${className || ''}`}
      width={resizable ? siderWidth : width}
      style={{
        position: 'relative',
        borderRight: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* 搜索激活时的全屏遮罩 */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: isSearchActive ? 'blur(12px)' : 'blur(0px)',
          zIndex: 1000,
          opacity: isSearchActive ? 1 : 0,
          pointerEvents: isSearchActive ? 'auto' : 'none',
          transition: 'all 0.3s ease',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsSearchActive(false);
        }}
      />

      {/* 固定顶部区域 */}
      <div style={{ flexShrink: 0 }}>
        <div
          style={{
            padding: '24px 10px 12px',
            position: 'relative',
            zIndex: isSearchActive ? 1001 : 10,
            transition: isSearchActive ? 'none' : 'z-index 0.3s step-end',
          }}
        >
          <div
            className="sidebar-search-box"
            style={{
              height: 40,
              borderRadius: 20,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'text',
              transform: 'translateZ(0)', // 开启硬件加速
              zIndex: 0, // 创建新的堆叠上下文
            }}
            onClick={() => setIsSearchActive(true)}
            onMouseEnter={(e) => {
              if (isSearchActive) return;
              const inner = e.currentTarget.querySelector('.search-inner') as HTMLElement;
              if (inner) {
                inner.style.backgroundColor = 'var(--ant-color-fill-tertiary)';
                inner.style.color = 'var(--ant-color-text-placeholder)';
              }
            }}
            onMouseLeave={(e) => {
              if (isSearchActive) return;
              const inner = e.currentTarget.querySelector('.search-inner') as HTMLElement;
              if (inner) {
                inner.style.backgroundColor = 'transparent';
                inner.style.color = 'var(--ant-color-text-placeholder)';
              }
            }}
          >
            {/* 旋转光效层 (仅激活时显示) */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '600px', // 足够大以覆盖旋转区域
                height: '600px',
                // 这里的 conic-gradient 控制光束的形状
                // transparent 0deg -> transparent 120deg: 前一段透明（控制光束其实位置）
                // rgba(...) 180deg: 光束最亮的地方（峰值）
                // transparent 240deg: 光束结束的地方
                // 通过调整角度差（例如 120deg -> 240deg = 120度宽），可以控制光束的"长短"
                background: `conic-gradient(
                  from 0deg,
                  transparent 20deg,
                  transparent 120deg,
                  rgba(255, 255, 255, 0.9) 150deg,
                  transparent 180deg
                )`,
                opacity: isSearchActive ? 1 : 0,
                transition: 'opacity 0.3s ease',
                animation: 'spinReverse 4s linear infinite',
                pointerEvents: 'none',
              }}
            />

            {/* search field 主体，内容遮盖层 */}
            <div
              className="search-inner"
              style={{
                position: 'absolute',
                inset: '1px', // 留出 1.5px 边框
                borderRadius: 19, // 20 - 1 (保持同心圆)
                border: '1.5px solid var(--custom-border)',
                backgroundColor: isSearchActive ? 'var(--content-bg)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px',
                color: 'var(--ant-color-text-placeholder)',
                transition: 'all 0.3s ease',
                zIndex: 1,
              }}
            >
              <MagnifyingGlassIcon style={{ fontSize: 18, marginRight: 7 }} />
              <span style={{ fontSize: 14 }}>全局搜索功能开发中...</span>
            </div>
          </div>
        </div>

        {/* 顶部插槽（导入进度条等） */}
        {headerSlot}

        {/* 顶部固定菜单 */}
        <div style={{ marginBottom: 12, paddingLeft: 12, paddingRight: 12, paddingTop: (showInstallExtensionButton || headerSlot) ? 0 : 24 }}>
          {topMenuItems.map((item) => {
            const isSelected =
              (item.key === 'recent' && selectedKey === 'recent') ||
              (item.key === 'bloggers' && selectedKey === 'bloggers') ||
              (item.key === 'starred' && selectedKey === 'starred') ||
              (item.key === 'uncategorized' && selectedKey === 'uncategorized') ||
              (item.key === 'video' && selectedKey === 'video');
            const isHovered = hoveredTopMenuItem === item.key;

            return (
              <div
                key={item.key}
                onClick={() => onSelect(item.key)}
                onMouseEnter={() => setHoveredTopMenuItem(item.key)}
                onMouseLeave={() => setHoveredTopMenuItem(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 16px',
                  marginBottom: 2,
                  borderRadius: 6,
                  cursor: 'pointer',
                  backgroundColor: isSelected
                    ? 'var(--ant-color-fill-secondary)'
                    : isHovered
                      ? 'var(--ant-color-fill-tertiary)'
                      : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', color: 'var(--ant-color-text)' }}>
                  {item.icon}
                </span>
                <span style={{ color: 'var(--ant-color-text)', fontWeight: 600, fontSize: '14px' }}>
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* 分割线 */}
        <div style={{
          margin: '0 16px 12px',
          height: 1,
          backgroundColor: 'var(--custom-border)',
        }} />

        {/* 分类标题 + 新建下拉菜单 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: '27px',
            paddingRight: '18px',
            paddingTop: '2px',
            paddingBottom: '6px',
            fontSize: '12px',
            color: 'var(--ant-color-text-tertiary)',
            gap: '6px',
          }}
        >
          <Dropdown
            menu={{
              items: createMenuItems,
              onClick: handleCreateMenuClick,
            }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <div style={{ position: 'relative', width: 20, height: 20, cursor: 'pointer' }}>
              {/* 弥散阴影层 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: 'linear-gradient(105deg, #ffb04f, #ff9447, #ff534d, #ff744a, #ffb04f)',
                  backgroundSize: '400% 400%',
                  animation: 'gradientShift 5s ease infinite',
                  filter: 'blur(6px)',
                  opacity: 0.6,
                  zIndex: 0,
                  pointerEvents: 'none',
                }}
              />
              <Button
                type="text"
                size="small"
                shape="circle"
                icon={<PlusOutlined style={{ fontSize: 10 }} />}
                style={{
                  width: 18,
                  height: 18,
                  minWidth: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  fontSize: '12px',
                  color: 'white',
                  background: 'linear-gradient(105deg, #ffb04f, #ff9447, #ff534d, #ff744a, #ffb04f)',
                  backgroundSize: '400% 400%',
                  animation: 'gradientShift 5s ease infinite',
                  border: 'none',
                  boxShadow: 'none',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
          </Dropdown>
          <span>标签分类</span>
        </div>
      </div>

      {/* 可滚动的标签列表 */}
      <div
        className="tag-list-scrollable custom-css-scrollbar"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '0 10px 10px' }}
      >
        {/* 渲染 Groups */}
        {tagGroups.map((group) => renderTagGroup(group))}

        {/* 未分组的独立 Tags */}
        {ungroupedTags.map((tag) => renderUngroupedTag(tag))}
      </div>

      {/* 拖拽调整宽度的 handle */}
      {resizable && (
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 4,
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: isResizing ? 'var(--ant-color-primary)' : 'transparent',
            borderRight: '1px solid var(--custom-border-secondary)',
            transition: isResizing ? 'none' : 'background-color 0.2s',
            zIndex: 100,
          }}
          onMouseEnter={(e) => {
            if (!isResizing) {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--ant-color-primary-bg)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }
          }}
        />
      )}
    </Sider>
  );
}

export default ClippingsSidebar;
